import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import useProgressBar from 'features/ad/progressBar/useProgressBar'
import 'moment/locale/ko'
import { batch, useDispatch, useSelector } from 'react-redux'
import { isEmptyObject } from 'utils'
import { useConfirm } from 'features/dialog/Confirm'
import { useHistory, useParams } from 'react-router-dom'
import classNames from 'classnames'
import Button from 'components/form/Button'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import {
  createAdGroup,
  fetchAdGroup,
  reset,
  setExceptionKeywords,
  updateAdGroup,
} from 'features/ad/group/adGroupSlice'
import {
  fetchCampaign,
  reset as campaignReset,
} from 'features/ad/campaign/campaignSlice'
import AdGroupAdvancedOptionsSummary from 'features/ad/group/AdGroupAdvancedOptionsSummary'
import useDashboardReferrer, {
  DASHBOARD_REFERRER,
} from 'features/ad/useDashboardReferrer'
import useAdGroupValidator from 'features/ad/group/hooks/useAdGroupValidator'
import ad from 'api/axios/foka/ad'
import { CampaignTypes } from 'features/Codes'
import AdGroupName from './form/AdGroupName'
import Placement from './form/Placement'
import Device from './form/Device'
import ExceptionKeywords from './form/ExceptionKeywords'
import MaxBiddingAmount from './form/MaxBiddingAmount'
import AdGroupDailyBudget from './form/AdGroupDailyBudget'
import Weights from './form/Weights'
import ContentMatchingMaxBiddingAmount from './form/ContentMatchingMaxBiddingAmount'
import ImpressionSchedule from './form/ImpressionSchedule'

const progress = {
  [DASHBOARD_REFERRER.CAMPAIGN]: [
    {
      text: '캠페인 만들기',
      done: true,
    },
    {
      text: '광고그룹 만들기',
      active: true,
    },
    {
      text: '키워드 만들기',
    },
    {
      text: '소재 선택하기',
    },
  ],
  [DASHBOARD_REFERRER.AD_GROUP]: [
    {
      text: '광고그룹 만들기',
      active: true,
    },
    {
      text: '키워드 만들기',
    },
    {
      text: '소재 선택하기',
    },
  ],
}

const AdGroup = ({ modify = false }) => {
  const referrer = useDashboardReferrer()
  useProgressBar(
    modify ? null : progress[referrer || DASHBOARD_REFERRER.CAMPAIGN]
  )
  const dispatch = useDispatch()
  const history = useHistory()
  const { accountId: adAccountId, campaignId, adGroupId } = useParams()
  const {
    campaign: {
      form: { campaignType },
      isLoading: isCampaignLoading,
    },
    adGroup: {
      isLoading: isAdGroupLoading,
      form: { keywordExtensionYn },
    },
  } = useSelector(state => state.ad)
  const isLoading = useMemo(
    () => isCampaignLoading || isAdGroupLoading,
    [isCampaignLoading, isAdGroupLoading]
  )
  const { errors, validate, addError } = useAdGroupValidator()
  const [isAdvancedOptionsOpen, setAdvancedOptionsOpen] = useState(false)
  const [PrevConfirm, showPrevConfirm] = useConfirm()

  useEffect(
    () => () => {
      batch(() => {
        dispatch(reset())
        dispatch(campaignReset())
      })
    },
    [dispatch]
  )

  const handleSubmit = async () => {
    if (keywordExtensionYn === 'N') {
      dispatch(setExceptionKeywords([]))
    }
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const fetcher = modify ? updateAdGroup() : createAdGroup()
        const { error, payload } = await dispatch(fetcher)
        if (error) {
          const { errorCode } = payload
          if (errorCode === 104002) {
            fail(
              '등록 가능한 광고그룹 개수를 초과하였습니다.\n미사용 광고그룹을 삭제하세요.'
            )
          } else if (errorCode === 104003) {
            addError({
              name: '상위 캠페인내에 이미 존재하는 광고그룹 이름입니다. 다시 입력하세요.',
            })
          } else {
            failWithErrorResponse(payload, {
              defaultMessage:
                '알 수 없는 이유로 광고그룹을 저장할 수 없습니다.\n다시 시도하세요.',
            })
          }
        } else {
          success(`광고그룹이 ${modify ? '수정' : '등록'}되었습니다.`)
          if (modify) {
            history.push(`/${adAccountId}/dashboard`)
          } else {
            history.push(
              `/${adAccountId}/ads/campaigns/${campaignId}/groups/${
                payload.adGroupDto.id
              }/keywords${referrer ? `?from=${referrer}` : ''}`
            )
          }
        }
      }
    } catch (ignore) {}
  }
  useEffect(() => {
    if (modify) {
      ;(async () => {
        const { error: campaignError, payload: campaignPayload } =
          await dispatch(fetchCampaign(campaignId))
        if (campaignError) {
          const { errorCode, errorMessage } = campaignPayload
          if (errorCode === 409) {
            fail('삭제된 캠페인 입니다.')
          } else {
            fail(errorMessage || '알 수 없는 에러가 발생했습니다')
          }
          history.push(`/${adAccountId}/dashboard`)
        } else {
          const { error: adGroupError, payload: adGroupPayload } =
            await dispatch(fetchAdGroup(adGroupId))
          if (adGroupError) {
            const { errorCode, errorMessage } = adGroupPayload
            if (errorCode === 409 || errorCode === 104007) {
              fail('삭제된 광고그룹 입니다.')
            } else {
              fail(errorMessage || '알 수 없는 에러가 발생했습니다')
            }
            history.push(`/${adAccountId}/dashboard`)
          }
        }
      })()
    }
  }, [adAccountId, adGroupId, campaignId, dispatch, history, modify])
  useEffect(() => {
    if (!modify) {
      ;(async () => {
        const { error, payload } = await dispatch(fetchCampaign(campaignId))
        if (error) {
          const { errorCode, errorMessage } = payload
          if (errorCode === 409) {
            fail('삭제된 캠페인 입니다.')
          } else {
            fail(errorMessage || '알 수 없는 에러가 발생했습니다')
          }
          history.push(`/${adAccountId}/dashboard`)
        } else {
          try {
            const {
              data: { generable },
            } = await ad.getAdGroupGenerable(campaignId)
            if (generable === false) {
              fail(
                '등록 가능한 광고그룹 개수를 초과하였습니다.\n미사용 광고그룹을 삭제하세요.'
              )
              history.push(`/${adAccountId}/dashboard`)
            }
          } catch (ignore) {}
        }
      })()
    }
  }, [adAccountId, campaignId, dispatch, history, modify])

  return (
    <>
      <LoadingWrapper isLoading={isLoading}>
        <div className="wrap_adcreate">
          <div className="ad_managebox">
            <div className="managebox_tit">
              <strong className="tit_box">광고그룹</strong>
            </div>
            <AdGroupName error={errors.name} />
            <Placement error={errors.placement} />
            <Device error={errors.device} />
            <ExceptionKeywords error={errors.exceptionKeywords} />
            <MaxBiddingAmount error={errors.maxBiddingAmount} />
            <AdGroupDailyBudget error={errors.dailyBudget} />
            <div
              className={classNames('box_option', {
                on: isAdvancedOptionsOpen,
              })}>
              <div
                className={classNames('managebox_tit', {
                  error:
                    errors.deviceWeight ||
                    errors.keywordExtensionWeight ||
                    errors.contentMatchingMaxBiddingAmount ||
                    errors.date ||
                    errors.weeklySchedule,
                })}
                onClick={() => setAdvancedOptionsOpen(!isAdvancedOptionsOpen)}>
                <strong className="tit_box">
                  고급
                  <span className="ico_fold" />
                </strong>
                {errors.deviceWeight ||
                errors.keywordExtensionWeight ||
                errors.contentMatchingMaxBiddingAmount ||
                errors.date ||
                errors.weeklySchedule ? (
                  <p className="txt_error">
                    잘못된 설정 내역이 있습니다. 다시 설정해 주세요.
                  </p>
                ) : modify ? (
                  <AdGroupAdvancedOptionsSummary />
                ) : (
                  <p className="desc_box">
                    고급 옵션에서{' '}
                    {campaignType === CampaignTypes.PREMIUM_LINK &&
                      '입찰 단가 상세, '}
                    집행기간과 요일/시간을 설정해보세요.
                  </p>
                )}
              </div>
              {campaignType === CampaignTypes.PREMIUM_LINK && (
                <>
                  <Weights errors={errors} />
                  <ContentMatchingMaxBiddingAmount
                    error={errors.contentMatchingMaxBiddingAmount}
                  />
                </>
              )}
              <ImpressionSchedule errors={errors} />
            </div>
          </div>
          <div className="page_btn">
            <div className="inner_btn">
              <Button className="btn_gb" onClick={() => showPrevConfirm()}>
                취소
              </Button>
              <Button
                className="btn_gb gb_bl"
                onClick={handleSubmit}
                loading={isLoading}>
                {modify ? '저장' : '저장하고 계속'}
              </Button>
            </div>
          </div>
        </div>
      </LoadingWrapper>
      <PrevConfirm
        title={modify ? '광고그룹 수정 취소' : '광고그룹 만들기 취소'}
        onClose={({ isConfirmed }) => {
          if (isConfirmed) {
            if (modify || referrer === DASHBOARD_REFERRER.AD_GROUP) {
              history.push(`/${adAccountId}/dashboard`)
            } else {
              history.push(
                `/${adAccountId}/afterCreate?type=${referrer}&campaignId=${campaignId}`
              )
            }
          }
        }}>
        광고그룹 {modify ? '수정을' : '만들기를'} 취소하시겠습니까?
        <br />
        저장되지 않은 모든 내용은 사라집니다.
      </PrevConfirm>
    </>
  )
}

AdGroup.propTypes = {
  modify: PropTypes.bool,
}

export default AdGroup
